// Live tracking — the manager's linked users (GET live-tracking/linked-users):
// people at the manager's structure node or below who share one of the
// manager's projects, each with their current attendance, last known location
// and that attendance's GPS trail. The same snapshot comes back in the
// socket's subscribe ack; live changes then arrive as socket events
// (see api/liveTrackingSocket.ts and hooks/useLiveTracking.ts).
// Also the user's own node areas (GET users/own-nodes) the live map draws
// under the pins, and one person's route history (POST linked-user-routes).

import { baseApi } from '../baseApi';
import { ROUTES } from '../routes';
import { ClockInState } from './attendanceApi';

/** What a point marks on its clock's trail — mirrors the backend's TrackType. */
export enum TrackType {
  /** The clock-in position. */
  StartTrack = 'start_track',
  /** The clock-out position. */
  StopTrack = 'stop_track',
  /** A fix the app tracked in between. */
  TrackPoint = 'track_point',
}

/** One GPS fix. `trackedAt` is when the device captured it (ISO). */
export interface LiveLocation {
  attendanceId: string;
  latitude: number;
  longitude: number;
  /** Metres, when the device reported it. */
  accuracy?: number;
  trackedAt: string;
  trackType: TrackType;
}

/** The clock a linked user is on (or most recently was on today). */
export interface LiveAttendance {
  attendanceId: string;
  state: ClockInState;
  /** ISO. */
  clockInDate?: string;
  /** ISO. */
  clockOutDate?: string;
}

export interface LinkedUser {
  companyUserId: string;
  fullName: string;
  title: string | null;
  attendance: LiveAttendance | null;
  lastLocation: LiveLocation | null;
  /** Oldest first. */
  trackLocations: LiveLocation[];
}

// ---- raw shapes (backend LinkedUser / socket payloads) ----
export interface ApiLinkedUser {
  companyUserId: string;
  firstName: string | null;
  lastName: string | null;
  title: string | null;
  attendance: LiveAttendance | null;
  lastLocation: LiveLocation | null;
  trackLocations: LiveLocation[];
}

/** `location:updated` — a linked user's new fixes (oldest first). */
export interface LocationUpdatedEvent {
  companyUserId: string;
  lastLocation: LiveLocation;
  points: LiveLocation[];
}

/** `attendance:updated` — one clock sync of a linked user: every synced clock
 *  (oldest first — the last is the current one) and the clock-in/out
 *  positions it stored. */
export interface AttendanceUpdatedEvent {
  companyUserId: string;
  attendances: LiveAttendance[];
  /** Oldest first. */
  points: LiveLocation[];
  /** null when the sync stored no position. */
  lastLocation: LiveLocation | null;
}

export const toLinkedUser = (u: ApiLinkedUser): LinkedUser => ({
  companyUserId: u.companyUserId,
  fullName: `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim(),
  title: u.title,
  attendance: u.attendance,
  lastLocation: u.lastLocation,
  trackLocations: u.trackLocations,
});

/** Merge new fixes into a trail: deduped by capture time, oldest first. */
export const mergeTrail = (trail: LiveLocation[], points: LiveLocation[]): LiveLocation[] => {
  const byTime = new Map(trail.map((p) => [p.trackedAt, p]));
  points.forEach((p) => byTime.set(p.trackedAt, p));
  return [...byTime.values()].sort((a, b) => a.trackedAt.localeCompare(b.trackedAt));
};

/** Apply a `location:updated` event. Fixes of another (older) clock only
 *  refresh the position, never the current clock's trail. */
export const applyLocationUpdate = (user: LinkedUser, event: LocationUpdatedEvent): LinkedUser => {
  const attendanceId = user.attendance?.attendanceId;
  const ownPoints = event.points.filter((p) => p.attendanceId === attendanceId);
  return {
    ...user,
    lastLocation: event.lastLocation,
    trackLocations: ownPoints.length
      ? mergeTrail(user.trackLocations, ownPoints)
      : user.trackLocations,
  };
};

/** Apply an `attendance:updated` event: the sync's last clock becomes the
 *  current one (a new clock starts a fresh trail) and that clock's
 *  clock-in/out positions join its trail. */
export const applyAttendanceUpdate = (
  user: LinkedUser,
  event: AttendanceUpdatedEvent,
): LinkedUser => {
  const attendance = event.attendances[event.attendances.length - 1] ?? user.attendance;
  const attendanceId = attendance?.attendanceId;
  const trail = user.attendance?.attendanceId === attendanceId ? user.trackLocations : [];
  const ownPoints = event.points.filter((p) => p.attendanceId === attendanceId);
  return {
    ...user,
    attendance,
    lastLocation: event.lastLocation ?? user.lastLocation,
    trackLocations: ownPoints.length ? mergeTrail(trail, ownPoints) : trail,
  };
};

export interface GeoPoint {
  latitude: number;
  longitude: number;
}

/** A linked user as the live-locations card and map render them. EVERY
 *  linked user is kept: offline people (no clock, or it ended) still list,
 *  and still pin when a last position is known. */
export interface LiveUser {
  companyUserId: string;
  fullName: string;
  /** On an open clock — started or paused. */
  online: boolean;
  /** The open clock's state while online (paused reads differently); null offline. */
  state: ClockInState | null;
  /** ISO — the open clock's check-in, while online. */
  clockInDate?: string;
  /** ISO — when the latest fix was captured ("last ping"); null without one. */
  lastPingAt: string | null;
  lastLocation: GeoPoint | null;
}

/** A LiveUser whose position is known — the only kind the map pins. */
export type PositionedLiveUser = LiveUser & { lastLocation: GeoPoint };

export const toLiveUsers = (users: LinkedUser[]): LiveUser[] =>
  users.map((u) => {
    const clock = u.attendance && u.attendance.state !== ClockInState.Ended ? u.attendance : null;
    return {
      companyUserId: u.companyUserId,
      fullName: u.fullName,
      online: clock !== null,
      state: clock?.state ?? null,
      clockInDate: clock?.clockInDate,
      lastPingAt: u.lastLocation?.trackedAt ?? null,
      lastLocation: u.lastLocation
        ? { latitude: u.lastLocation.latitude, longitude: u.lastLocation.longitude }
        : null,
    };
  });

// ---- route history (POST live-tracking/linked-user-routes) ----

/** The route drawer's presets — this endpoint's OWN vocabulary (the
 *  attendance list's AttendanceDateFilter has no yesterday/custom). */
export enum RouteDateFilter {
  Today = 'today',
  Yesterday = 'yesterday',
  Last7Days = 'last_7_days',
  Last30Days = 'last_30_days',
  Custom = 'custom',
}

export interface LinkedUserRoutesArgs {
  userId: string;
  dateFilter: RouteDateFilter;
  /** ISO — sent only with `custom`: the chosen From day's start. */
  startDate?: string;
  /** ISO — sent only with `custom`: the chosen To day's end. */
  endDate?: string;
}

/** One clock in the range and the fixes it recorded, oldest first. */
export interface UserRoute {
  attendance: LiveAttendance;
  points: LiveLocation[];
}

export interface UserRoutes {
  companyUserId: string;
  fullName: string;
  /** Oldest clock first. */
  routes: UserRoute[];
}

/** Confirmed response (user-pasted 2026-11-04). */
interface ApiLinkedUserRoutes {
  companyUserId: string;
  firstName: string | null;
  lastName: string | null;
  title: string | null;
  routes: {
    attendance: {
      attendanceId: string;
      state: ClockInState;
      clockInDate: string;
      clockOutDate: string | null;
    };
    trackLocations: LiveLocation[];
  }[];
}

const byTrackedAt = (a: LiveLocation, b: LiveLocation) => a.trackedAt.localeCompare(b.trackedAt);

export const toUserRoutes = (res: ApiLinkedUserRoutes): UserRoutes => ({
  companyUserId: res.companyUserId,
  fullName: `${res.firstName ?? ''} ${res.lastName ?? ''}`.trim(),
  routes: res.routes.map((r) => ({
    attendance: {
      attendanceId: r.attendance.attendanceId,
      state: r.attendance.state,
      clockInDate: r.attendance.clockInDate,
      clockOutDate: r.attendance.clockOutDate ?? undefined,
    },
    // Oldest first — defensively, the timeline and gap maths assume it.
    points: [...r.trackLocations].sort(byTrackedAt),
  })),
});

// ---- own node areas (GET users/own-nodes) ----

/** Confirmed response (user-pasted 2026-11-04): the signed-in user's
 *  accessible structure nodes. `coordinates` is the structure form's
 *  "lat,lng" string; `radius` is its geofence in metres. */
interface ApiOwnNode {
  _id: string;
  name: string;
  coordinates: string | null;
  radius: number | null;
}

/** A node the live map draws: a dot at its centre, a circle when a radius
 *  is set. Nodes without coordinates never become one. */
export interface NodeArea {
  id: string;
  name: string;
  center: GeoPoint;
  /** Metres; null draws the dot only. */
  radius: number | null;
}

/** "lat,lng" / "lat, lng" → point; null for anything unparseable or out of
 *  range (|lat| ≤ 90, |lng| ≤ 180). */
export const parseCoordinates = (text: string): GeoPoint | null => {
  const parts = text.split(',');
  if (parts.length !== 2) return null;
  const [latitude, longitude] = parts.map((p) => Number(p.trim()));
  if (parts.some((p) => p.trim() === '')) return null;
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
  if (Math.abs(latitude) > 90 || Math.abs(longitude) > 180) return null;
  return { latitude, longitude };
};

/** Keeps only the nodes with parseable coordinates — the others never draw. */
export const toNodeAreas = (nodes: ApiOwnNode[]): NodeArea[] =>
  nodes.flatMap((n) => {
    const center = n.coordinates === null ? null : parseCoordinates(n.coordinates);
    return center ? [{ id: n._id, name: n.name, center, radius: n.radius }] : [];
  });

export const liveTrackingApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getLinkedUsers: build.query<LinkedUser[], void>({
      query: () => ({ ...ROUTES.liveTracking.linkedUsers }),
      transformResponse: (res: ApiLinkedUser[]) => res.map(toLinkedUser),
      providesTags: [{ type: 'Attendance', id: 'LIVE' }],
    }),
    getLinkedUserRoutes: build.query<UserRoutes, LinkedUserRoutesArgs>({
      query: (body) => ({ ...ROUTES.liveTracking.linkedUserRoutes, data: body }),
      transformResponse: toUserRoutes,
      providesTags: [{ type: 'Attendance', id: 'ROUTES' }],
    }),
    // Tagged 'Structure' so a node edit (which invalidates it) refreshes the areas.
    getOwnNodes: build.query<NodeArea[], void>({
      query: () => ({ ...ROUTES.users.ownNodes }),
      transformResponse: toNodeAreas,
      providesTags: ['Structure'],
    }),
  }),
});

export const { useGetLinkedUsersQuery, useGetLinkedUserRoutesQuery, useGetOwnNodesQuery } =
  liveTrackingApi;
